import { AnimatePresence, motion } from 'framer-motion';

import {
  ArrowLeft,
  ArrowRight,
  BookMarked,
  BookOpen,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Globe,
  HelpCircle,
  Target,
  Zap
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import api from "../config/axiosConfig";
import HeroCompact from './HeroCompact';

const C = {
  bg:        '#020617',
  surface:   'rgba(15,23,42,0.7)',
  border:    'rgba(255,255,255,0.06)',
  text:      '#f1f5f9',
  textSub:   '#94a3b8',
  textFaint: '#475569',
  cyan:      '#22d3ee',
  amber:     '#f59e0b',
  emerald:   '#10b981',
  rose:      '#fb7185',
};

const M = ({ children, size = 11, color = C.textFaint, w = false, style = {} }) => (
  <span style={{ fontFamily:"'Roboto Mono', monospace", fontSize:size, letterSpacing:'0.03em', color, fontWeight:w?700:400, lineHeight:1, ...style }}>
    {children}
  </span>
);

function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

const DETAIL_CSS = `
  @keyframes pulse-dot {
    0%,100% { opacity:.4; transform:scale(1); }
    50%      { opacity:1;  transform:scale(1.4); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  .sections-nav-btn {
    display:flex; align-items:center; gap:8px;
    padding:10px 18px;
    background:none; border:none;
    border-bottom:2px solid transparent;
    cursor:pointer;
    font-family:'Roboto Mono',monospace;
    font-size:10px; letter-spacing:0.1em; text-transform:uppercase;
    transition:all 0.2s; white-space:nowrap; flex-shrink:0; position:relative;
  }
  .sections-nav-btn.active { border-bottom-color: var(--accent); color: var(--accent); }
  .sections-nav-btn:not(.active) { color: #94a3b8; }
  .sections-nav-btn:not(.active):hover { color: #e2e8f0; background:rgba(255,255,255,0.02); }
  .slide-line {
    display:flex; align-items:stretch; cursor:pointer;
    transition:background 0.15s; border-radius:2px;
  }
  .slide-line:hover  { background:rgba(255,255,255,0.04); }
  .slide-line.active { background:rgba(34,211,238,0.06); }
  .ex-card {
    border:1px solid rgba(255,255,255,0.06);
    border-radius:10px; overflow:hidden;
    background:rgba(8,12,28,0.8);
    transition:border-color 0.2s, box-shadow 0.2s;
  }
  .ex-card:hover:not(.ex-open) { border-color:rgba(255,255,255,0.1); }
  .ex-card.ex-done  { border-color:rgba(16,185,129,0.25); }
  .ex-card.ex-open  { border-color:var(--accent-30); box-shadow:0 0 32px rgba(0,0,0,0.4); }
  .ex-textarea {
    width:100%; background:transparent; border:none; outline:none;
    color:#94a3b8; font-family:'Plus Jakarta Sans',sans-serif;
    font-size:14px; line-height:1.85; resize:none;
    min-height:120px; padding:14px 16px;
    border-left:2px solid transparent; transition:border-color 0.15s, color 0.15s;
    box-sizing:border-box;
  }
  .ex-textarea:focus { border-left-color:var(--accent); color:#e2e8f0; }
  .ex-textarea::placeholder { color:rgba(71,85,105,0.6); }
  .res-card {
    display:flex; flex-direction:column; gap:10px;
    padding:16px 18px;
    border:1px solid rgba(255,255,255,0.06); border-radius:10px;
    background:rgba(8,12,28,0.8); cursor:pointer;
    transition:all 0.2s; position:relative; overflow:hidden;
  }
  .res-card:hover {
    border-color:var(--accent-30);
    background:rgba(8,12,28,0.97);
    transform:translateY(-2px);
    box-shadow:0 10px 28px rgba(0,0,0,0.45);
  }
  .res-card::before {
    content:''; position:absolute; inset:0;
    background:linear-gradient(135deg, var(--accent-06) 0%, transparent 55%);
    opacity:0; transition:opacity 0.2s;
  }
  .res-card:hover::before { opacity:1; }
  .prog-segment {
    height:4px; border-radius:2px;
    transition:background 0.35s, width 0.35s;
  }
  @media (max-width:767px) {
    .theory-cols  { flex-direction:column !important; height:auto !important; overflow:visible !important; }
    .theory-side  { width:100% !important; border-right:none !important; border-bottom:1px solid rgba(255,255,255,0.06) !important; max-height:none !important; }
    .res-grid     { grid-template-columns:1fr !important; }
  }
`;

const TIMING_CONF = {
  avant:   { label: 'Avant la session',   color: C.amber,   bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)'  },
  pendant: { label: 'Pendant la session', color: C.cyan,    bg: 'rgba(34,211,238,0.08)',  border: 'rgba(34,211,238,0.2)'  },
  après:   { label: 'Après la session',   color: C.emerald, bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.2)'  },
};

const TheorySection = ({ atelier, accent }) => {
  const [idx, setIdx] = useState(0);
  const isMobile = useIsMobile(768);
  const slides = atelier.theory?.slides || [];
  const total  = slides.length;
  const slide  = slides[idx];

  const go = useCallback((d) => setIdx(v => Math.max(0, Math.min(total-1, v+d))), [total]);

  useEffect(() => {
    const h = (e) => {
      if (e.key==='ArrowRight'||e.key==='ArrowDown') go(1);
      if (e.key==='ArrowLeft' ||e.key==='ArrowUp')   go(-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [go]);

  if (!slide) return <div style={{ padding:40, textAlign:'center', opacity:0.3 }}><M>Aucun_contenu</M></div>;

  return (
    <div style={{ display:'flex', height: isMobile?'auto':'540px' }} className="theory-cols">
      <div className="theory-side" style={{ width:220, flexShrink:0, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', overflowY:'auto', background:'rgba(4,8,20,0.6)' }}>
        <div style={{ padding:'10px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <div style={{ width:5, height:5, borderRadius:'50%', background:accent, boxShadow:`0 0 6px ${accent}`, animation:'pulse-dot 2s ease-in-out infinite' }}/>
          <M size={9} color={C.textFaint}>PLAN · {total} SLIDES</M>
        </div>
        {isMobile ? (
          <div style={{ display:'flex', gap:6, padding:'10px 12px', overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            {slides.map((s,i) => (
              <button key={i} onClick={()=>setIdx(i)}
                style={{ flexShrink:0, padding:'5px 12px', background:idx===i?`${accent}18`:'transparent', border:`1px solid ${idx===i?`${accent}40`:C.border}`, borderRadius:4, cursor:'pointer', fontFamily:"'Roboto Mono',monospace", fontSize:9, color:idx===i?accent:C.textFaint, whiteSpace:'nowrap', transition:'all 0.15s' }}>
                {String(i+1).padStart(2,'0')} {s.title.slice(0,16)}{s.title.length>16?'…':''}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ flex:1, overflowY:'auto', padding:'6px 0' }}>
            {slides.map((s,i) => (
              <div key={i} className={`slide-line${idx===i?' active':''}`} onClick={()=>setIdx(i)}>
                <div style={{ width:36, flexShrink:0, display:'flex', alignItems:'flex-start', justifyContent:'flex-end', paddingRight:10, paddingTop:11 }}>
                  <M size={10} color={idx===i?`${accent}90`:'rgba(71,85,105,0.4)'}>{String(i+1).padStart(2,'0')}</M>
                </div>
                <div style={{ width:2, flexShrink:0, background:idx===i?accent:'transparent', transition:'background 0.15s' }}/>
                <div style={{ flex:1, padding:'10px 12px', minWidth:0 }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, fontWeight:idx===i?600:400, color:idx===i?C.text:C.textFaint, lineHeight:1.4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', transition:'color 0.15s' }}>
                    {s.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:'hidden' }}>
        <div style={{ height:2, background:'rgba(255,255,255,0.04)', flexShrink:0 }}>
          <motion.div animate={{ width:`${((idx+1)/total)*100}%` }} transition={{ duration:0.35, ease:'easeOut' }}
            style={{ height:'100%', background:`linear-gradient(90deg, ${accent}, ${accent}70)`, boxShadow:`0 0 8px ${accent}50` }}/>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding: isMobile?'24px 20px':'40px 48px', position:'relative' }}>
          <div style={{ position:'absolute', top: isMobile?16:28, right: isMobile?16:32 }}>
            <M size={9} color={`${accent}45`}>{String(idx+1).padStart(2,'0')} / {String(total).padStart(2,'0')}</M>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-6 }} transition={{ duration:0.2 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
                <div style={{ height:1, width:20, background:`linear-gradient(90deg, ${accent}60, transparent)` }}/>
                <M size={9} color={`${accent}65`} style={{ letterSpacing:'0.14em', textTransform:'uppercase' }}>Théorie · Slide {idx+1}</M>
              </div>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile?22:28, fontWeight:700, color:C.text, margin:'0 0 22px', lineHeight:1.2, letterSpacing:'-0.02em', paddingRight:60 }}>
                {slide.title}
              </h2>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile?14:15, color:C.textSub, lineHeight:1.9, whiteSpace:'pre-wrap', maxWidth:680 }}>
                {slide.content}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div style={{ flexShrink:0, borderTop:`1px solid ${C.border}`, padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(2,6,23,0.5)', gap:12 }}>
          <button onClick={()=>go(-1)} disabled={idx===0}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'none', border:`1px solid ${idx===0?'rgba(255,255,255,0.04)':`${accent}25`}`, color:idx===0?C.textFaint:accent, cursor:idx===0?'default':'pointer', fontFamily:"'Roboto Mono',monospace", fontSize:9, letterSpacing:'0.1em', opacity:idx===0?0.4:1, transition:'all 0.15s', borderRadius:2 }}>
            <ChevronLeft size={12}/> PRÉC
          </button>
          <div style={{ display:'flex', gap:5, alignItems:'center' }}>
            {slides.map((_,i) => (
              <button key={i} onClick={()=>setIdx(i)}
                style={{ width:idx===i?20:6, height:6, borderRadius:3, background:idx===i?accent:'rgba(255,255,255,0.12)', border:'none', cursor:'pointer', transition:'all 0.25s', boxShadow:idx===i?`0 0 8px ${accent}60`:'none', padding:0 }}/>
            ))}
          </div>
          <button onClick={()=>go(1)} disabled={idx===total-1}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:idx<total-1?`${accent}12`:'none', border:`1px solid ${idx<total-1?`${accent}35`:'rgba(255,255,255,0.04)'}`, color:idx<total-1?accent:C.textFaint, cursor:idx<total-1?'pointer':'default', fontFamily:"'Roboto Mono',monospace", fontSize:9, letterSpacing:'0.1em', opacity:idx===total-1?0.4:1, transition:'all 0.15s', borderRadius:2 }}>
            SUIV <ArrowRight size={12}/>
          </button>
        </div>
      </div>
    </div>
  );
};

const ExercisesSection = ({ atelier, accent }) => {
  const [answers,  setAnswers]  = useState({});
  const [openId,   setOpenId]   = useState(null);
  const [saved,    setSaved]    = useState({});
  const isMobile  = useIsMobile(768);
  const debRefs   = useRef({});
  const taRefs    = useRef({});

  const exercises = atelier.exercises || [];
  const doneCount = exercises.filter(ex => (answers[ex.id]||'').trim().length > 10).length;
  const progress  = exercises.length > 0 ? (doneCount / exercises.length) * 100 : 0;

  const getState = ex => {
    const v = (answers[ex.id]||'').trim();
    if (v.length > 10) return 'done';
    if (v.length > 0)  return 'active';
    return 'empty';
  };

  const stateColors = { done:C.emerald, active:accent, empty:C.textFaint };
  const stateIcons  = {
    done:   <CheckCircle2 size={14} color={C.emerald}/>,
    active: <Circle size={14} color={accent} style={{ opacity:0.7 }}/>,
    empty:  <Circle size={14} color={C.textFaint} style={{ opacity:0.3 }}/>,
  };

  const handleChange = (id, val) => {
    setAnswers(p => ({ ...p, [id]:val }));
    setSaved(p => ({ ...p, [id]:false }));
    if (debRefs.current[id]) clearTimeout(debRefs.current[id]);
    debRefs.current[id] = setTimeout(() => setSaved(p => ({ ...p, [id]:true })), 800);
  };

  useEffect(() => {
    if (openId && taRefs.current[openId]) {
      setTimeout(() => taRefs.current[openId]?.focus(), 220);
    }
  }, [openId]);

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <M size={9} color={C.textFaint}>PROGRESSION</M>
            <div style={{ display:'flex', gap:4 }}>
              {exercises.map(ex => {
                const st = getState(ex);
                return <div key={ex.id} className="prog-segment" style={{ width:20, background:st==='done'?C.emerald:st==='active'?accent:'rgba(255,255,255,0.08)' }}/>;
              })}
            </div>
          </div>
          <M size={9} color={doneCount>0?C.emerald:C.textFaint}>{doneCount}/{exercises.length} complétés</M>
        </div>
        <div style={{ height:2, background:'rgba(255,255,255,0.05)', borderRadius:1, overflow:'hidden' }}>
          <motion.div animate={{ width:`${progress}%` }} transition={{ duration:0.5, ease:'easeOut' }}
            style={{ height:'100%', background:`linear-gradient(90deg, ${accent}, ${C.emerald})`, borderRadius:1 }}/>
        </div>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {exercises.map((ex, i) => {
          const state  = getState(ex);
          const isOpen = openId === ex.id;
          const color  = stateColors[state];

          return (
            <motion.div key={ex.id} layout
              className={`ex-card${state==='done'?' ex-done':''}${isOpen?' ex-open':''}`}
              style={{ '--accent-30':`${accent}30`, '--accent':accent }}>
              <div onClick={() => setOpenId(isOpen ? null : ex.id)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', cursor:'pointer', userSelect:'none' }}>
                <div style={{ width:32, height:32, flexShrink:0, border:`1px solid ${color}30`, background:`${color}08`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Roboto Mono',monospace", fontSize:11, fontWeight:700, color, borderRadius:4, transition:'all 0.2s' }}>
                  {String(i+1).padStart(2,'0')}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:600, color:isOpen?C.text:C.textSub, transition:'color 0.15s', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {ex.title}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                    <M size={8} color={`${accent}60`}>{ex.type?.toUpperCase()}</M>
                    {state !== 'empty' && <>
                      <div style={{ width:1, height:8, background:'rgba(255,255,255,0.1)' }}/>
                      <M size={8} color={color}>{state==='done'?'Complété':'En cours'}</M>
                    </>}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                  {stateIcons[state]}
                  <motion.div animate={{ rotate:isOpen?90:0 }} transition={{ duration:0.2 }}>
                    <ChevronRight size={14} color={C.textFaint}/>
                  </motion.div>
                </div>
              </div>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.25, ease:'easeInOut' }} style={{ overflow:'hidden' }}>
                    <div style={{ borderTop:`1px solid ${C.border}` }}>
                      <div style={{ padding:'16px 16px 12px', background:`${accent}04` }}>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                          <div style={{ width:2, flexShrink:0, background:`${accent}40`, borderRadius:1, alignSelf:'stretch', minHeight:20 }}/>
                          <p style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile?13:14, color:C.textSub, lineHeight:1.75, margin:0 }}>
                            {ex.prompt}
                          </p>
                        </div>
                      </div>
                      <div style={{ position:'relative', borderTop:`1px solid ${C.border}` }}>
                        <AnimatePresence>
                          {saved[ex.id] && answers[ex.id]?.trim() && (
                            <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }}
                              style={{ position:'absolute', top:10, right:12, zIndex:1, display:'flex', alignItems:'center', gap:4, padding:'2px 8px', background:`${C.emerald}10`, border:`1px solid ${C.emerald}25`, borderRadius:3 }}>
                              <CheckCircle2 size={9} color={C.emerald}/>
                              <M size={8} color={C.emerald}>Sauvegardé</M>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <textarea
                          ref={el => taRefs.current[ex.id] = el}
                          className="ex-textarea"
                          value={answers[ex.id]||''}
                          onChange={e => handleChange(ex.id, e.target.value)}
                          placeholder="Votre réponse..."
                          rows={5}
                          style={{ '--accent':accent }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {doneCount === exercises.length && exercises.length > 0 && (
          <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
            style={{ marginTop:24, padding:'16px 20px', border:`1px solid ${C.emerald}25`, background:`${C.emerald}06`, borderRadius:8, display:'flex', alignItems:'center', gap:12 }}>
            <CheckCircle2 size={20} color={C.emerald}/>
            <div>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, color:C.emerald, marginBottom:2 }}>Tous les exercices complétés</div>
              <M size={9} color={`${C.emerald}70`}>Partagez-les lors de la session live avec votre intervenant.</M>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ResourcesSection = ({ atelier, accent }) => {
  const isMobile  = useIsMobile(768);
  const resources = atelier.resources || [];

  const typeConf = {
    pdf:  { icon:<FileText size={15}/>,   label:'PDF',      color:C.rose,  bg:'rgba(251,113,133,0.08)' },
    link: { icon:<Globe size={15}/>,      label:'Lien web', color:C.cyan,  bg:'rgba(34,211,238,0.08)'  },
    doc:  { icon:<BookMarked size={15}/>, label:'Document', color:C.amber, bg:'rgba(245,158,11,0.08)'  },
  };
  const getTC = r => typeConf[r.type] || typeConf.link;

  // Grouper par timing si au moins une ressource a un timing
  const hasTimings = resources.some(r => r.timing);
  const timingOrder = ['avant', 'pendant', 'après'];

  const grouped = hasTimings
    ? timingOrder.reduce((acc, t) => {
        const items = resources.filter(r => r.timing === t);
        if (items.length) acc.push({ timing: t, items });
        return acc;
      }, [])
    : [{ timing: null, items: resources }];

  if (!resources.length) return (
    <div style={{ padding:'40px 0', textAlign:'center', opacity:0.3 }}>
      <M size={11}>Aucune_ressource_disponible</M>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:28 }}>
      {grouped.map(({ timing, items }) => {
        const tc = timing ? TIMING_CONF[timing] : null;
        return (
          <div key={timing || 'all'}>
            {tc && (
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                <div style={{ width:4, height:16, background:tc.color, borderRadius:2 }}/>
                <Clock size={12} color={tc.color}/>
                <M size={9} color={tc.color} w>{tc.label.toUpperCase()}</M>
              </div>
            )}
            <div className="res-grid" style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'repeat(2,1fr)', gap:10 }}>
              {items.map((res, i) => {
                const rtc = getTC(res);
                return (
                  <motion.div key={i}
                    initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.05 }}
                    className="res-card"
                    onClick={() => res.url && window.open(res.url,'_blank')}
                    style={{ '--accent-30':`${accent}30`, '--accent-06':`${accent}06` }}>

                    {/* Header : type badge + action icon */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', position:'relative', zIndex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:30, height:30, background:rtc.bg, border:`1px solid ${rtc.color}25`, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', color:rtc.color }}>
                          {rtc.icon}
                        </div>
                        <div style={{ padding:'2px 8px', background:rtc.bg, border:`1px solid ${rtc.color}20`, borderRadius:3 }}>
                          <M size={8} color={rtc.color}>{rtc.label}</M>
                        </div>
                      </div>
                      {res.url
                        ? <ExternalLink size={11} color={C.textFaint} style={{ opacity:0.5 }}/>
                        : <Download size={11} color={C.textFaint} style={{ opacity:0.5 }}/>
                      }
                    </div>

                    {/* Titre */}
                    <div style={{ position:'relative', zIndex:1 }}>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, color:C.text, lineHeight:1.4, marginBottom:4 }}>
                        {res.title}
                      </div>
                      {res.url && (
                        <M size={9} color={C.textFaint} style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block', maxWidth:'100%' }}>
                          {res.url.replace(/^https?:\/\//,'').slice(0,42)}{res.url.length>47?'…':''}
                        </M>
                      )}
                    </div>

                    {/* Context — la nouveauté */}
                    {res.context && (
                      <div style={{ position:'relative', zIndex:1, padding:'8px 10px', background:'rgba(255,255,255,0.02)', border:`1px solid ${C.border}`, borderRadius:6 }}>
                        <p style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:C.textFaint, lineHeight:1.6, margin:0 }}>
                          {res.context}
                        </p>
                      </div>
                    )}

                    <div style={{ height:1, background:`linear-gradient(90deg, ${accent}30, transparent)`, position:'relative', zIndex:1 }}/>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PrepSection = ({ atelier, accent }) => {
  const warmup  = atelier.warmupQuestions  || [];
  const agenda  = atelier.sessionAgenda    || [];
  const outcomes = atelier.learningOutcomes || [];
  const isMobile = useIsMobile(768);

  const agendaTypeColors = {
    intro:    accent,
    theory:   C.cyan,
    exercise: C.emerald,
    practice: C.amber,
    closing:  C.rose,
  };

  if (!warmup.length && !agenda.length && !outcomes.length) return (
    <div style={{ padding:40, textAlign:'center', opacity:0.3 }}>
      <M>Aucune information de préparation disponible</M>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:32 }}>

      {/* ── Objectifs d'apprentissage ── */}
      {outcomes.length > 0 && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
            <div style={{ width:4, height:18, background:accent, borderRadius:2 }}/>
            <Target size={13} color={accent}/>
            <M size={10} color={accent} w>CE QUE VOUS SAUREZ FAIRE</M>
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'repeat(2,1fr)', gap:10 }}>
            {outcomes.map((outcome, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px', background:'rgba(8,12,28,0.8)', border:`1px solid ${C.border}`, borderRadius:10 }}>
                <div style={{ width:22, height:22, flexShrink:0, background:`${accent}12`, border:`1px solid ${accent}25`, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', marginTop:1 }}>
                  <M size={10} color={accent} w>{String(i+1)}</M>
                </div>
                <p style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:C.textSub, lineHeight:1.6, margin:0 }}>
                  {outcome}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Questions d'échauffement ── */}
      {warmup.length > 0 && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
            <div style={{ width:4, height:18, background:accent, borderRadius:2 }}/>
            <HelpCircle size={13} color={accent}/>
            <M size={10} color={accent} w>QUESTIONS D'ÉCHAUFFEMENT</M>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {warmup.map((q, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'16px 18px', background:'rgba(8,12,28,0.8)', border:`1px solid ${C.border}`, borderRadius:10 }}>
                <div style={{ flexShrink:0, width:28, height:28, background:`${accent}10`, border:`1px solid ${accent}25`, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <M size={12} color={accent} w>{i+1}</M>
                </div>
                <p style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, color:C.textSub, lineHeight:1.65, margin:0 }}>
                  {q}
                </p>
              </div>
            ))}
          </div>
          {/* Note contextuelle */}
          <div style={{ marginTop:12, padding:'10px 14px', background:`${accent}06`, border:`1px solid ${accent}15`, borderRadius:8, display:'flex', alignItems:'flex-start', gap:10 }}>
            <div style={{ width:4, height:4, borderRadius:'50%', background:accent, marginTop:6, flexShrink:0 }}/>
            <M size={9} color={`${accent}70`}>
              Réfléchissez à ces questions avant la session live — elles servent de point de départ à votre intervenant.
            </M>
          </div>
        </div>
      )}

      {/* ── Agenda de session ── */}
      {agenda.length > 0 && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
            <div style={{ width:4, height:18, background:accent, borderRadius:2 }}/>
            <Calendar size={13} color={accent}/>
            <M size={10} color={accent} w>DÉROULÉ DE LA SESSION</M>
          </div>
          <div style={{ border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', background:'rgba(8,12,28,0.6)' }}>
            {agenda.map((item, i) => {
              const dotColor = agendaTypeColors[item.type] || C.textFaint;
              return (
                <div key={i} style={{ display:'flex', alignItems:'stretch', borderBottom: i !== agenda.length-1 ? `1px solid ${C.border}` : 'none' }}>
                  {/* Barre couleur type */}
                  <div style={{ width:3, flexShrink:0, background:dotColor, opacity:0.6 }}/>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:16, padding:'14px 20px', flex:1 }}>
                    {/* Timing */}
                    <div style={{ minWidth:70, flexShrink:0 }}>
                      <M size={10} color={dotColor} w>{item.time}</M>
                    </div>
                    {/* Step */}
                    <div style={{ flex:1 }}>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:500, color:C.text, lineHeight:1.45 }}>
                        {item.step}
                      </div>
                      {item.type && (
                        <div style={{ marginTop:4, display:'inline-flex', padding:'2px 8px', background:`${dotColor}10`, border:`1px solid ${dotColor}20`, borderRadius:3 }}>
                          <M size={8} color={dotColor} style={{ textTransform:'uppercase', letterSpacing:'0.1em' }}>{item.type}</M>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AtelierDetail = ({ atelier, onBack, isUnlocked, handleVideoAction, isHost = false }) => {
  const [activeSection, setActiveSection] = useState('prep');
  const [atelierVideo, setAtelierVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const isMobile = useIsMobile(768);
  const accent   = atelier.accentColor || C.cyan;

  useEffect(() => {
    if (!atelier?.slug) return;
    const fetchAtelierVideo = async () => {
      setLoadingVideo(true);
      try {
        const response = await api.get(`/videos/ateliers/${atelier.slug}`);
        setAtelierVideo(response.data);
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Erreur chargement vidéo atelier:', error);
        }
        setAtelierVideo(null);
      } finally {
        setLoadingVideo(false);
      }
    };
    fetchAtelierVideo();
  }, [atelier?.slug]);

  const SECTIONS = [
    { id:'prep',      label:'Agenda & Prépa', icon:Calendar  },
    { id:'theory',    label:'Théorie',         icon:BookOpen  },
    { id:'exercises', label:'Exercices',        icon:Zap       },
    { id:'resources', label:'Ressources',       icon:FileText  },
  ];

  return (
    <div className="detail-content" style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflowY:'auto', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{DETAIL_CSS}</style>

      {/* Breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
        <button onClick={onBack}
          style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', color:C.textFaint, padding:0, transition:'color 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.color=C.textSub}
          onMouseLeave={e=>e.currentTarget.style.color=C.textFaint}>
          <ArrowLeft size={13}/><M size={10}>Bibliothèque</M>
        </button>
        <ChevronRight size={11} color={C.textFaint}/>
        <M size={10} color={accent} style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{atelier.title}</M>
      </div>

      {/* Hero compact */}
      <HeroCompact
        atelier={atelier}
        accent={accent}
        isUnlocked={isUnlocked}
        isHost={isHost}
        handleVideoAction={handleVideoAction}
        isMobile={isMobile}
        atelierVideo={atelierVideo}
      />

      {/* Navigation sections */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, marginBottom:22, overflowX:'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none', '--accent':accent }}>
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={()=>setActiveSection(id)}
            className={`sections-nav-btn${activeSection===id?' active':''}`}
            style={{ '--accent':accent }}>
            <Icon size={13}/>
            {label}
            {activeSection===id && (
              <span style={{ position:'absolute', bottom:-3, left:'50%', transform:'translateX(-50%)', width:5, height:5, borderRadius:'50%', background:accent, boxShadow:`0 0 10px ${accent}60` }}/>
            )}
          </button>
        ))}
      </div>

      {/* Contenu sections */}
      <AnimatePresence mode="wait">
        <motion.div key={activeSection} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-4 }} transition={{ duration:0.16 }}>

          {activeSection==='prep' && (
            <div style={{ border:`1px solid ${C.border}`, borderRadius:12, padding: isMobile?16:24, background:'rgba(8,12,28,0.4)' }}>
              <PrepSection atelier={atelier} accent={accent}/>
            </div>
          )}

          {activeSection==='theory' && (
            <div style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
              <TheorySection atelier={atelier} accent={accent}/>
            </div>
          )}

          {activeSection==='exercises' && (
            <ExercisesSection atelier={atelier} accent={accent}/>
          )}

          {activeSection==='resources' && (
            <ResourcesSection atelier={atelier} accent={accent}/>
          )}

        </motion.div>
      </AnimatePresence>

      <div style={{ height:28 }}/>
    </div>
  );
};

export default AtelierDetail;